import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';

/** Anything a deep link can point at. */
interface Identifiable {
  id: number;
}

export interface FocusTargetConfig<T extends Identifiable> {
  /** Query param carrying the id, e.g. 'meetingId'. */
  param: string;
  /** The list to search, read fresh on every attempt. */
  items: () => T[];
  /** What to do with the match — open a dialog, scroll to a row, expand a card. */
  onFocus: (item: T) => void;
}

export interface FocusTarget {
  /**
   * Retry the lookup. Call whenever new data lands, including from a live
   * update — a link pointing at something the page hasn't fetched yet misses
   * silently otherwise.
   */
  check: () => void;
}

/**
 * Wires `?<param>=<id>` to an action on the matching item, so a notification can
 * open the thing it is about instead of dropping the user on a list.
 *
 * Handles the parts every feature shares: reading the id as a stream, holding it
 * until the list loads, and clearing it once used. Which view state to change,
 * and what to do with the match, stay with the page.
 *
 * The lookup only searches a list the page already fetched, and those are scoped
 * to the current user server-side, so an id belonging to someone else finds
 * nothing.
 *
 * @param config
 * @returns {FocusTarget}
 */
export function injectFocusTarget<T extends Identifiable>({
  param,
  items,
  onFocus,
}: FocusTargetConfig<T>): FocusTarget {
  const router = inject(Router);
  const route = inject(ActivatedRoute);
  const destroyRef = inject(DestroyRef);

  let wantedId: number | null = null;

  const check = (): void => {
    if (wantedId === null) return;

    const match = items().find((item) => item.id === wantedId);
    // Not loaded yet, or not on this page — check() runs again after the fetch.
    if (!match) return;

    wantedId = null;

    // Merge, so an unrelated param such as a search query survives.
    router.navigate([], {
      relativeTo: route,
      queryParams: { [param]: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });

    onFocus(match);
  };

  // A stream, not a snapshot: a second notification changes the param without
  // re-running the host component's ngOnInit.
  route.queryParamMap.pipe(takeUntilDestroyed(destroyRef)).subscribe((params) => {
    const id = Number(params.get(param));
    wantedId = Number.isInteger(id) && id > 0 ? id : null;
    check();
  });

  return { check };
}
